import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";

interface CreditPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentBalance: number;
  minimumPayment: number;
  checkingBalance: number;
  onPay: (amount: number) => Promise<void>;
}

const CreditPaymentModal = ({
  open, onOpenChange, currentBalance, minimumPayment, checkingBalance, onPay,
}: CreditPaymentModalProps) => {
  const [amount, setAmount] = useState("");
  const [paying, setPaying] = useState(false);

  const numAmount = parseInt(amount) || 0;
  const maxPayable = Math.min(currentBalance, checkingBalance);

  const handlePay = async () => {
    if (numAmount <= 0 || numAmount > maxPayable) return;
    setPaying(true);
    try {
      await onPay(numAmount);
      toast.success(`Credit payment of $${numAmount} applied!`);
      setAmount("");
      onOpenChange(false);
    } catch (e: unknown) {
      toast.error(getErrorMessage(e) || "Payment failed");
    } finally {
      setPaying(false);
    }
  };

  const setQuickAmount = (val: number) => setAmount(String(Math.min(val, maxPayable)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Make Credit Payment</DialogTitle>
          <DialogDescription>
            Pay from your Checking account to reduce your credit balance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">Credit Balance</p>
              <p className="font-heading font-bold text-foreground">${currentBalance.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Checking Available</p>
              <p className="font-heading font-bold text-foreground">${checkingBalance.toLocaleString()}</p>
            </div>
          </div>

          {minimumPayment > 0 && (
            <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
              💡 Minimum payment due: <strong>${minimumPayment}</strong>. Paying more than minimum helps your credit score.
            </p>
          )}

          <div className="flex gap-2">
            {minimumPayment > 0 && (
              <Button variant="outline" size="sm" onClick={() => setQuickAmount(minimumPayment)} className="text-xs">
                Min ${minimumPayment}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setQuickAmount(currentBalance)} className="text-xs">
              Full ${currentBalance}
            </Button>
          </div>

          <Input
            type="number"
            placeholder="Enter payment amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min={1}
            max={maxPayable}
          />

          <Button
            onClick={handlePay}
            disabled={paying || numAmount <= 0 || numAmount > maxPayable}
            className="w-full"
          >
            {paying ? "Processing..." : `Pay $${numAmount || 0}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreditPaymentModal;
